import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  Button, 
  Paper,
  List,
  ListItem,
  ListItemIcon,
  Fade,
  Grow,
  Zoom,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  Business as BusinessIcon, 
  Storefront, 
  Diamond, 
  Grass,
  Phone,
  Email,
  LocationOn,
  CheckCircle,
  Security,
  SupportAgent,
  ArrowForward
} from '@mui/icons-material';

// Import images
import businessHero from '../images/z.jpg';

// Define color palette
const colors = {
  primary: '#132714', // Dark green
  secondary: '#57a649', // Vibrant green
  accent: '#f5a623', // Gold accent
  lightBg: '#f8fffb', // Very light green
  textDark: '#132714',
  textLight: '#ffffff'
};

const Business = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const accountTypes = [
    {
      icon: <Grass sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "SMME Green Account",
      description: "Perfect for small and medium agricultural enterprises",
      features: [
        "Automatically qualify upon document verification",
        "Activation fee of E200",
        "Monthly Maintenance fee of E100",
        "6 free ATM withdrawals per month"
      ],
      action: "View Details",
      color: 'linear-gradient(135deg, #57a649 0%, #3d7a33 100%)'
    },
    {
      icon: <Storefront sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Commercial Emerald Account",
      description: "For established businesses with growing needs",
      features: [
        "Annual revenues of E5 Million+",
        "Activation fee of E500",
        "Monthly Maintenance fee of E120",
        "12 free ATM withdrawals per month"
      ],
      action: "View Details",
      color: 'linear-gradient(135deg, #50C878 0%, #228B22 100%)'
    },
    {
      icon: <Diamond sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Enterprise Diamond Account",
      description: "Premium services for large corporations",
      features: [
        "Annual revenues of E50 Million+",
        "Activation fee of E1500",
        "Monthly Maintenance fee of E150",
        "Unlimited ATM withdrawals"
      ],
      action: "View Details",
      color: 'linear-gradient(135deg, #b9f2ff 0%, #7ec4cf 100%)'
    }
  ];

  const features = [
    {
      icon: <BusinessIcon sx={{ fontSize: 32, color: colors.secondary }} />,
      title: "Business Expertise",
      description: "Bank with professionals who understand business needs"
    },
    {
      icon: <Security sx={{ fontSize: 32, color: colors.secondary }} />,
      title: "Advanced Security",
      description: "Enterprise-grade protection for your business accounts"
    },
    {
      icon: <SupportAgent sx={{ fontSize: 32, color: colors.secondary }} />,
      title: "Dedicated Support",
      description: "Priority assistance from business banking specialists"
    }
  ];

  return (
    <Box sx={{ overflowX: 'hidden', backgroundColor: colors.lightBg }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: '80vh',
          minHeight: '600px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `linear-gradient(to right, rgba(19, 39, 20, 0.9), rgba(87, 166, 73, 0.7)), url(${businessHero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '100px',
            background: `linear-gradient(to top, ${colors.lightBg}, transparent)`,
            zIndex: 1
          }
        }}
      >
        <Container>
          <Fade in timeout={800}>
            <Box>
              <Typography 
                variant="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 800,
                  fontSize: isMobile ? '2.5rem' : '3.5rem',
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  mb: 3,
                  lineHeight: 1.2
                }}
              >
                <Box component="span" sx={{ color: colors.accent }}>Business</Box> Banking Solutions
              </Typography>
              <Typography 
                variant="h5"
                sx={{ 
                  maxWidth: '800px',
                  mx: 'auto',
                  mb: 4,
                  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  fontSize: isMobile ? '1.1rem' : '1.4rem',
                  fontWeight: 300,
                  opacity: 0.9
                }}
              >
                Tailored financial solutions to help your business grow and thrive
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  size="large"
                  href="/apply"
                  sx={{ 
                    px: 5,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: '50px',
                    boxShadow: `0 4px 20px ${colors.secondary}80`,
                    '&:hover': {
                      boxShadow: `0 6px 25px ${colors.secondary}b3`,
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                    backgroundColor: colors.secondary,
                    fontWeight: 600
                  }}
                >
                  Open Business Account
                </Button>
                <Button 
                  variant="outlined" 
                  size="large"
                  href="/contact"
                  sx={{ 
                    px: 5,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderWidth: '2px'
                    },
                    transition: 'all 0.3s ease',
                    fontWeight: 600
                  }}
                >
                  Contact Business Advisor
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container sx={{ py: 8 }}>
        {/* Business Banking Section */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Fade in timeout={1000}>
              <Box>
                <Typography 
                  variant="h3" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 700,
                    color: colors.primary,
                    mb: 3,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -10,
                      left: 0,
                      width: '80px',
                      height: '4px',
                      backgroundColor: colors.secondary,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Business Banking
                </Typography>
                <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontWeight: 400 }}>
                  Comprehensive financial solutions for your enterprise
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: 'text.secondary', mb: 3 }}>
                  Manage your business finances efficiently with our specialized banking services. 
                  From cash management to payroll solutions, we provide the tools your business needs to succeed.
                </Typography>
                <List dense sx={{ mb: 3 }}>
                  {[
                    "Dedicated business relationship managers",
                    "Bulk payment processing",
                    "Customized reporting tools",
                    "Multi-user access controls"
                  ].map((item, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle sx={{ color: colors.secondary }} />
                      </ListItemIcon>
                      <Typography variant="body1" sx={{ color: colors.textDark }}>{item}</Typography>
                    </ListItem>
                  ))}
                </List>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    href="/apply"
                    sx={{
                      px: 4,
                      py: 1.25,
                      fontSize: '0.95rem',
                      borderRadius: '8px',
                      boxShadow: `0 4px 12px ${colors.secondary}80`,
                      backgroundColor: colors.secondary,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#4a8f3d'
                      }
                    }}
                  >
                    Open Business Account
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    href="/features"
                    sx={{
                      px: 4,
                      py: 1.25,
                      fontSize: '0.95rem',
                      borderRadius: '8px',
                      borderWidth: '2px',
                      borderColor: colors.secondary,
                      color: colors.secondary,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: `${colors.secondary}08`,
                        borderWidth: '2px'
                      }
                    }}
                  >
                    View Business Features
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Grid>
         </Grid>

        {/* Account Types Section */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: colors.secondary,
                fontWeight: 600,
                letterSpacing: 1,
                mb: 1
              }}
            >
              BUSINESS SOLUTIONS
            </Typography>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700,
                color: colors.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80px',
                  height: '4px',
                  background: colors.secondary,
                  borderRadius: '2px'
                }
              }}
            >
              Business Accounts
            </Typography>
            <Typography variant="h5" sx={{ mt: 3, color: 'text.secondary', fontWeight: 300 }}>
              Choose the right account for your business needs
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            {accountTypes.map((account, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Zoom in timeout={800} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Card
                    elevation={3}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      background: 'white',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.04)',
                      border: `1px solid ${colors.secondary}20`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 12px 28px rgba(0,0,0,0.08)',
                        borderColor: `${colors.secondary}40`,
                        transform: 'translateY(-8px)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        height: 120,
                        background: account.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          width: '100%',
                          height: '16px',
                          background: 'linear-gradient(to bottom, transparent, white)'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 70,
                          height: 70,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255,255,255,0.3)',
                          backdropFilter: 'blur(5px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                        }}
                      >
                        {account.icon}
                      </Box>
                    </Box>
                    <Box sx={{ p: 3, flexGrow: 1 }}>
                      <Typography variant="h5" gutterBottom sx={{ 
                        color: colors.primary, 
                        fontWeight: 600, 
                        textAlign: 'center',
                        fontSize: '1.4rem'
                      }}>
                        {account.title}
                      </Typography>
                      <Typography variant="body1" paragraph sx={{ 
                        color: 'text.secondary', 
                        textAlign: 'center', 
                        mb: 2,
                        fontSize: '0.95rem'
                      }}>
                        {account.description}
                      </Typography>
                      <List dense sx={{ mb: 2 }}>
                        {account.features.map((feature, i) => (
                          <ListItem key={i} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckCircle sx={{ color: colors.secondary, fontSize: 18 }} />
                            </ListItemIcon>
                            <Typography variant="body2" sx={{ 
                              color: colors.textDark,
                              fontSize: '0.9rem'
                            }}>
                              {feature}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        href={`/business/${account.title.toLowerCase().replace(/\s+/g, '-')}`}
                        sx={{
                          borderRadius: '50px',
                          py: 1,
                          backgroundColor: colors.secondary,
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          '&:hover': {
                            backgroundColor: '#4a8f3d'
                          }
                        }}
                      >
                        {account.action}
                      </Button>
                    </Box>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Features Section - Single Column */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: colors.secondary,
                fontWeight: 600,
                letterSpacing: 1,
                mb: 1
              }}
            >
              BUSINESS BENEFITS
            </Typography>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700,
                color: colors.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80px',
                  height: '4px',
                  background: colors.secondary,
                  borderRadius: '2px'
                }
              }}
            >
              Why Choose Farmers Bank?
            </Typography>
          </Box>
          <Grid container spacing={2} justifyContent="center">
            {features.map((feature, index) => (
              <Grid item xs={12} sm={10} md={8} key={index}>
                <Grow in timeout={800} style={{ transitionDelay: `${index * 150}ms` }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      display: 'flex',
                      alignItems: 'center',
                      textAlign: 'left',
                      borderRadius: '12px',
                      background: 'white',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                      border: `1px solid ${colors.secondary}20`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                        borderColor: `${colors.secondary}40`,
                        transform: 'translateY(-3px)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: `${colors.secondary}10`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 3,
                        flexShrink: 0
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ 
                        color: colors.primary, 
                        fontWeight: 600,
                        mb: 1
                      }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.95rem'
                      }}>
                        {feature.description}
                      </Typography>
                    </Box>
                  </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            backgroundColor: colors.primary,
            color: 'white',
            borderRadius: '16px',
            p: 6,
            mb: 6,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: 'radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
              opacity: 0.3
            }
          }}
        >
          <Fade in timeout={800}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Grow Your Business With Us
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, fontWeight: 300 }}>
                Let's discuss how we can support your business goals
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  href="/apply"
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: '50px',
                    boxShadow: `0 4px 20px ${colors.accent}80`,
                    backgroundColor: colors.accent,
                    color: colors.primary,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#f5b840'
                    }
                  }}
                >
                  Apply Now
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="/contact"
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  Schedule Consultation
                </Button>
              </Box>
            </Box>
          </Fade>
        </Box>

        {/* Contact Section */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: '16px',
            backgroundColor: '#f8fffb',
            border: `1px solid ${colors.secondary}20`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.04)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ 
            color: colors.primary, 
            fontWeight: 700,
            mb: 3
          }}>
            Business Banking Support
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Phone sx={{ color: colors.secondary, mr: 2, fontSize: '1.2rem' }} />
              <Typography variant="body1" sx={{ color: colors.textDark }}>
                +268 2505 0888 (Business Banking)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Email sx={{ color: colors.secondary, mr: 2, fontSize: '1.2rem' }} />
              <Typography variant="body1" sx={{ color: colors.textDark }}>
                business@farmersbank.co.sz
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationOn sx={{ color: colors.secondary, mr: 2, fontSize: '1.2rem' }} />
              <Typography variant="body1" sx={{ color: colors.textDark }}>
                Mhlakuvane Street, Manzini, Eswatini
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            href="/locations"
            endIcon={<ArrowForward />}
            sx={{
              borderRadius: '50px',
              px: 4,
              py: 1.25,
              borderWidth: '2px',
              borderColor: colors.secondary,
              color: colors.secondary,
              fontWeight: 600,
              fontSize: '0.95rem',
              '&:hover': {
                backgroundColor: `${colors.secondary}08`
              }
            }}
          >
            Find a Business Center
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default Business;